import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TopBar from 'src/components/molecules/TopBar';
import TopBarBackButton from 'src/components/atoms/TopBarBackButton';
import TopBarHomeButton from 'src/components/atoms/TopBarHomeButton';
import InfoTerm from '../../components/organisms/PageElements/InfoTerm/InfoTerm';
import InputText from '../../components/organisms/PageElements/InputElements/InputText/InputText';
import SelfObservationMoodInput from '../../components/atoms/SelfObservationMoodInput';
import MySwiper from '../../components/organisms/Swiper/MySwiper';
import { SelfObservationDayDB } from '../../data/datatypes';
import { createOrUpdateSelfObservationDay, getSelfObservationDay } from '../../services/DBService/DbService';
import { useAutoSaveOnPageLeave } from '../../store/hooks';

interface State {
    moodLevel: 1 | 2 | 3 | 4 | 5 | undefined;
    activityLevel: 1 | 2 | 3 | 4 | 5 | undefined;
    foodLevel: 1 | 2 | 3 | 4 | 5 | undefined;
    sleepLevel: 1 | 2 | 3 | 4 | 5 | undefined;

    lookBack: string;
    lookAhead: string;
    notes: string;
}

const initialState: State = {
    moodLevel: undefined,
    activityLevel: undefined,
    foodLevel: undefined,
    sleepLevel: undefined,

    lookBack: '',
    lookAhead: '',
    notes: ''
};

type Action = {
    type: 'MOOD_CLICKED',
    payload: 1 | 2 | 3 | 4 | 5 | undefined
} | {
    type: 'ACTIVITY_CLICKED',
    payload: 1 | 2 | 3 | 4 | 5 | undefined
} | {
    type: 'FOOD_CLICKED',
    payload: 1 | 2 | 3 | 4 | 5 | undefined
} | {
    type: 'SLEEP_CLICKED',
    payload: 1 | 2 | 3 | 4 | 5 | undefined
} | {
    type: 'LOOKBACK_CHANGED',
    payload: string
} | {
    type: 'LOOKAHEAD_CHANGED',
    payload: string
} | {
    type: 'NOTES_CHANGED',
    payload: string
} | {
    type: 'INIT',
    payload?: SelfObservationDayDB
};

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'MOOD_CLICKED':
            return {
                ...state,
                moodLevel: action.payload
            };
        case 'ACTIVITY_CLICKED':
            return {
                ...state,
                activityLevel: action.payload
            };
        case 'FOOD_CLICKED':
            return {
                ...state,
                foodLevel: action.payload
            };
        case 'SLEEP_CLICKED':
            return {
                ...state,
                sleepLevel: action.payload
            };
        case 'LOOKBACK_CHANGED':
            return {
                ...state,
                lookBack: action.payload
            };
        case 'LOOKAHEAD_CHANGED':
            return {
                ...state,
                lookAhead: action.payload
            };
        case 'NOTES_CHANGED':
            return {
                ...state,
                notes: action.payload
            };
        case 'INIT':
            if (!action.payload) {
                return initialState;
            } else {
                return {
                    moodLevel: action.payload.moodLevel,
                    activityLevel: action.payload.activityLevel,
                    foodLevel: action.payload.foodLevel,
                    sleepLevel: action.payload.sleepLevel,
                    lookBack: action.payload.lookBack || '',
                    lookAhead: action.payload.lookAhead || '',
                    notes: action.payload.notes || '',
                };
            }
        default:
            return state;
    }
};

type Properties = {
};

const SelfObservationDayOverview = (props: Properties) => {
    const navigate = useNavigate();
    const { isoDate } = useParams();
    if (!isoDate) {
        throw new Error('No Date found');
    }
    const date = new Date(isoDate);

    const [state, dispatch] = React.useReducer(reducer, initialState);

    useAutoSaveOnPageLeave(
        [state],
        async () => {
            const day: SelfObservationDayDB = {
                _id: `selfobservation_${isoDate}`,
                moodLevel: state.moodLevel,
                activityLevel: state.activityLevel,
                foodLevel: state.foodLevel,
                sleepLevel: state.sleepLevel,

                lookBack: state.lookBack !== '' ? state.lookBack : undefined,
                lookAhead: state.lookAhead !== '' ? state.lookAhead : undefined,
                notes: state.notes !== '' ? state.notes : undefined,
            };
            createOrUpdateSelfObservationDay(day);
        },
        [isoDate],
        async () => {
            const fetchedDayData = await getSelfObservationDay(`selfobservation_${isoDate}`);
            dispatch({
                type: 'INIT',
                payload: fetchedDayData
            });
        }
    );

    const readableDateString = date.toLocaleDateString();
    return (
        <div className="content hunger">
            <TopBar
                title="Tagebuch"
                icon={'icon_protocol.svg'}
                leftButtons={[
                    <TopBarHomeButton />,
                    <TopBarBackButton onClick={() => navigate('/self-observation')} />
                ]}
            />
            <MySwiper
                afterPrevSwipe={() => navigate('/self-observation')}
            >
                <div className="content-container content-self-observation-day">
                    <h1>{readableDateString}</h1>
                    <InfoTerm
                        data={{
                            type: 'info_term',
                            term: 'Stimmung',
                            desc: 'Wie hast Du Dich heute gefühlt?'
                        }}
                    />
                    <SelfObservationMoodInput
                        value={state.moodLevel}
                        setValue={value => dispatch({ type: 'MOOD_CLICKED', payload: value })}
                    />
                    <InfoTerm
                        data={{
                            type: 'info_term',
                            term: 'Bewegung',
                            desc: 'Wie zufrieden warst Du heute mit Deinem Bewegungsverhalten?'
                        }}
                    />
                    <SelfObservationMoodInput
                        value={state.activityLevel}
                        setValue={value => dispatch({ type: 'ACTIVITY_CLICKED', payload: value })}
                    />
                    <InfoTerm
                        data={{
                            type: 'info_term',
                            term: 'Essen',
                            desc: 'Wie zufrieden warst Du heute mit Deinem Essverhalten? '
                        }}
                    />
                    <SelfObservationMoodInput
                        value={state.foodLevel}
                        setValue={value => dispatch({ type: 'FOOD_CLICKED', payload: value })}
                    />
                    <InfoTerm
                        data={{
                            type: 'info_term',
                            term: 'Schlaf',
                            desc: 'Wie schätzt Du Deinen Schlaf in der letzten Nacht ein?'
                        }}
                    />
                    <SelfObservationMoodInput
                        value={state.sleepLevel}
                        setValue={value => dispatch({ type: 'SLEEP_CLICKED', payload: value })}
                    />
                    <InfoTerm
                        data={{
                            type: 'info_term',
                            term: 'Blick zurück',
                            desc: 'Ist heute etwas passiert, was einen Einfluss auf Deine Stimmung, Deine Bewegung, Dein Essverhalten oder Deinen Schlaf hatte?'
                        }}
                    />
                    <InputText
                        data={{ type: 'input_text', multiline: true }}
                        value={state.lookBack}
                        setValue={value => dispatch({ type: 'LOOKBACK_CHANGED', payload: value })}
                    />
                    <InfoTerm
                        data={{
                            type: 'info_term',
                            term: 'Blick nach vorne',
                            desc: 'Was nimmst Du Dir für morgen vor, um für Dich zu sorgen?'
                        }}
                    />
                    <InputText
                        data={{ type: 'input_text', multiline: true }}
                        value={state.lookAhead}
                        setValue={value => dispatch({ type: 'LOOKAHEAD_CHANGED', payload: value })}
                    />
                    <InfoTerm
                        data={{
                            type: 'info_term',
                            term: 'Notizen zum Tag',
                            desc: 'Trage hier alles ein, was für Dich noch wichtig ist (z.B. das war heute besonders schön, Gewicht, körperliche Verfassung, …).'
                        }}
                    />
                    <InputText
                        data={{ type: 'input_text', multiline: true }}
                        value={state.notes}
                        setValue={value => dispatch({ type: 'NOTES_CHANGED', payload: value })}
                    />
                </div>
            </MySwiper>
        </div>
    );
};
export default SelfObservationDayOverview;
